/* eslint-disable react/display-name */
import React, { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronRight } from "react-icons/fa";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSearchParams } from "react-router";
import useDisplayStickyButton from "~/hook/useDisplayStickyButton";
import { useFontStyle } from "~/hook/useFontStyle";
import useIsClient from "~/hook/useIsClient";
import { useLanguageParams } from "~/hook/useLanguageParams";
import useLocale from "~/hook/useLocale";
import { RecipeData, RecipeListViewProps } from "~/interface/recipe/recipe";
import { useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { MstPersonType } from "~/type";
import { LANG } from "~/utils/constant";
import { ApiService } from "~/utils/service";
import Chip from "../shared-components/Chip";
import ImageWithSkeleton from "../shared-components/ImageWithSkeleton";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import NoHasMoreText from "../shared-components/NoHasMoreText";
import Tag from "../shared-components/Tag";

function RecipeListView({ recipeData, selectedCategory, responseCount }: RecipeListViewProps) {
  const { recipeCategories } = useAppSelector((state) => state.recipe);
  const { showTopButton } = useDisplayStickyButton();
  const { t } = useTranslation();

  const lang = useLanguageParams();
  const apiSerice = new ApiService(lang || LANG.english);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategoryState, setSelectedCategoryState] = useState<MstPersonType | null>(selectedCategory || null);

  const [allrecipeData, setallrecipeData] = useState<RecipeData[]>(recipeData);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(responseCount > recipeData.length ? true : false);
  const [pageIndex, setPageIndex] = useState(1);

  const handleFetchNextPageData = async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    try {
      const res = await apiSerice.getRecipeData({
        pageIndex: pageIndex + 1,
        pageSize: 36,
        subCategory: selectedCategoryState?.id ?? null,
      });
      const newRecipes = res?.rows || [];
      if (newRecipes.length === 0) {
        setHasMore(false);
      } else {
        setallrecipeData((prev) => [...prev, ...newRecipes]);
        setPageIndex((prev) => prev + 1);
        allrecipeData.length === responseCount && setHasMore(false);
      }
    } catch {
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectedCategory = (category: string | null) => {
    setPageIndex(1);
    setHasMore(true);

    const newParams = new URLSearchParams(searchParams); // clone current params

    if (category === selectedCategoryState?.text?.toLowerCase()) {
      setSelectedCategoryState(null);
      newParams.delete("category"); // remove category only
    } else {
      newParams.set("category", category?.toLowerCase() || ""); // update or set category
    }

    setSearchParams(newParams, { replace: true }); // preserve view=list view and others
  };

  useEffect(() => {
    if (recipeData.length === 0) {
      setHasMore(false);
      return;
    }
    if (responseCount <= recipeData.length) {
      setHasMore(false);
    } else {
      setHasMore(true);
    }
    setallrecipeData(recipeData);
  }, [recipeData, responseCount]);

  useEffect(() => {
    setSelectedCategoryState(selectedCategory);
  }, [selectedCategory]);

  return (
    <div className="w-full flex flex-col gap-3 ">
      <div className={cn(showTopButton ? "filter-chip-animation my-5" : "filter-chip-non-animation my-5")}>
        <Chip
          chipData={recipeCategories.map((category) => category?.text?.toLowerCase() || "")}
          selectedValue={selectedCategoryState?.text ? selectedCategoryState?.text?.toLowerCase() : ""}
          onClick={(value) => handleSelectedCategory(value.toString())}
        />
      </div>

      <InfiniteScroll
        dataLength={allrecipeData.length}
        next={handleFetchNextPageData}
        hasMore={hasMore}
        loader={
          <div className="w-full flex justify-center items-center">
            <LoadingSpinner />
          </div>
        }
        endMessage={
          <div className="w-full flex justify-center items-center">
            <NoHasMoreText
              className="text-textSecondaryColor"
              text={t("common.seeAllData", { data: lang === LANG.english ? "Recipes" : "વાનગીઓ" })}
            />
          </div>
        }
        scrollThreshold={0.6}
      >
        <div className="w-full grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 max-md:px-3">
          {allrecipeData?.map((recipe, index) => {
            return (
              <React.Fragment key={recipe.id}>
                <RecipeListViewCard recipe={recipe} />
              </React.Fragment>
            );
          })}
        </div>
      </InfiniteScroll>
    </div>
  );
}

export const RecipeListViewCard = memo(({ recipe }: { recipe: RecipeData }) => {
  const { LocalizedLink } = useLocale();
  const imageUrl = "https://legacy.swaminarayanbhagwan.org/wp-content/uploads/sb_uploads/thal/recipe_images/R0142.jpg";
  const { normalInter, medium } = useFontStyle();
  const isClient = useIsClient();
  const imageUrlSet = useMemo(
    () => ({
      small: recipe.img?.s?.url || imageUrl,
      medium: recipe.img?.m?.url || imageUrl,
      large: recipe.img?.l?.url || imageUrl,
      hd: recipe.img?.hd?.url || imageUrl,
      original: recipe.img?.og?.url || imageUrl,
    }),
    [recipe.img],
  );

  return (
    <LocalizedLink
      to={`/thal/${recipe.old_id}`}
      state={{ isListView: true }}
      className="bg-cardBg rounded-xl p-4 space-y-3 cursor-pointer relative h-full w-full"
    >
      <div className="w-full aspect-square rounded-md bg-slate-300 overflow-hidden ">
        <ImageWithSkeleton
          url={imageUrlSet}
          alt={recipe.name || "Recipe Image"}
          className="w-full h-full object-cover"
          blur_hash={recipe.img?.bh}
          isPreloaded={!isClient}
        />
      </div>
      <div className="w-full flex justify-between items-center">
        <div className="">
          <h3 className={cn("body_text_m text-textColor font-medium font-baloobhai2Medium")}>{recipe.name}</h3>
          {recipe.cook_time_min && (
            <p className={cn("body_text_s text-textColor font-normal font-baloobhai2Medium  ")}>{recipe.cook_time_min} mins. to cook</p>
          )}
        </div>
        <div className="min-w-[2.2rem] min-h-[2.2rem] bg-primary rounded-full flex items-center justify-center cursor-pointer">
          <FaChevronRight size={15} className="text-textHeadingColor" />
        </div>
      </div>
      {recipe.tag && (
        <div className="absolute top-2 left-5">
          <Tag text={recipe?.tag} />
        </div>
      )}
    </LocalizedLink>
  );
});

export default memo(RecipeListView);
